// src/modules/accounting/api.js
import API from '../../services/api';

const BASE = '/accounting';

/* ── Dashboard ── */
export const getAccountingDashboard = (params) =>
  API.get(`${BASE}/dashboard`, { params }).then((r) => r.data);

export const getEnhancedDashboard = (params) =>
  API.get(`${BASE}/dashboard/enhanced`, { params }).then((r) => r.data);

/* ── Double-Entry: Chart of Accounts ── */
export const getAccounts = (params) =>
  API.get(`${BASE}/accounts`, { params }).then((r) => r.data);

export const getAccountTree = () =>
  API.get(`${BASE}/accounts/tree`).then((r) => r.data);

export const getAccountHierarchy = (id) =>
  API.get(`${BASE}/accounts/${id}/hierarchy`).then((r) => r.data);

export const getAccountByCode = (code) =>
  API.get(`${BASE}/accounts/by-code/${code}`).then((r) => r.data);

export const createAccount = (data) =>
  API.post(`${BASE}/accounts`, data).then((r) => r.data);

export const updateAccount = (id, data) =>
  API.put(`${BASE}/accounts/${id}`, data).then((r) => r.data);

export const bootstrapAccounts = () =>
  API.post(`${BASE}/accounts/bootstrap`).then((r) => r.data);

/* ── Posting Rules (which account each automatic entry hits) ── */
export const getPostingRules = () =>
  API.get(`${BASE}/posting-rules`).then((r) => r.data);

export const seedPostingRules = () =>
  API.post(`${BASE}/posting-rules/seed`).then((r) => r.data);

export const validatePostingRules = (changes) =>
  API.post(`${BASE}/posting-rules/validate`, { changes }).then((r) => r.data);

export const createPostingRuleChange = (data) =>
  API.post(`${BASE}/posting-rules/change-requests`, data).then((r) => r.data);

export const listPostingRuleChanges = (params) =>
  API.get(`${BASE}/posting-rules/change-requests`, { params }).then((r) => r.data);

export const cancelPostingRuleChange = (id) =>
  API.post(`${BASE}/posting-rules/change-requests/${id}/cancel`).then((r) => r.data);

export const getPostingRuleHistory = (ruleKey) =>
  API.get(`${BASE}/posting-rules/${ruleKey}/history`).then((r) => r.data);

/* ── Double-Entry: Journal Entries ── */
export const getJournalEntries = (params) =>
  API.get(`${BASE}/journal-entries`, { params }).then((r) => r.data);

export const getJournalEntryById = (id) =>
  API.get(`${BASE}/journal-entries/${id}`).then((r) => r.data);

export const createJournalEntry = (data) =>
  API.post(`${BASE}/journal-entries`, data).then((r) => r.data);

/* Delete and reverse are approval-gated: these submit a request, they do not
   change the ledger. The entry is only touched once an approver signs off. */
export const reverseJournalEntry = (id, reason = '') =>
  API.post(`${BASE}/journal-entries/${id}/reverse`, { reason }).then((r) => r.data);

export const deleteJournalEntry = (id, reason = '') =>
  API.delete(`${BASE}/journal-entries/${id}`, { data: { reason } }).then((r) => r.data);

export const getJournalActionRequests = (params) =>
  API.get(`${BASE}/journal-entries/action-requests`, { params }).then((r) => r.data);

export const getPendingJournalActions = () =>
  API.get(`${BASE}/journal-entries/action-requests/pending-map`).then((r) => r.data);

export const cancelJournalActionRequest = (id) =>
  API.patch(`${BASE}/journal-entries/action-requests/${id}/cancel`).then((r) => r.data);

export const getJournalByReference = (params) =>
  API.get(`${BASE}/journal-entries/by-reference`, { params }).then((r) => r.data);

/* ── Smelter settlements (dispatch picker inside a manual journal) ── */
export const getOutstandingSmelterSettlements = (params) =>
  API.get(`${BASE}/smelter-settlements/outstanding`, { params }).then((r) => r.data);

/* ── Manual Journal Entries (approval-gated) ── */
export const createManualJournal = (data) =>
  API.post(`${BASE}/manual-journal`, data).then((r) => r.data);

export const listManualJournals = (params) =>
  API.get(`${BASE}/manual-journal`, { params }).then((r) => r.data);

export const getManualJournalById = (id) =>
  API.get(`${BASE}/manual-journal/${id}`).then((r) => r.data);

export const cancelManualJournal = (id) =>
  API.post(`${BASE}/manual-journal/${id}/cancel`).then((r) => r.data);

/* ── Double-Entry: General Ledger ── */
export const getAccountLedger = (accountId, params) =>
  API.get(`${BASE}/ledger/${accountId}`, { params }).then((r) => r.data);

export const getAccountBalance = (accountId, params) =>
  API.get(`${BASE}/ledger/${accountId}/balance`, { params }).then((r) => r.data);

export const getAllAccountBalances = (params) =>
  API.get(`${BASE}/ledger/balances/all`, { params }).then((r) => r.data);

export const getCashBook = (params) =>
  API.get(`${BASE}/ledger/cash-book`, { params }).then((r) => r.data);

/* ── Double-Entry: Trial Balance ── */
export const getTrialBalance = (params) =>
  API.get(`${BASE}/trial-balance`, { params }).then((r) => r.data);

export const getGroupedTrialBalance = (params) =>
  API.get(`${BASE}/trial-balance/grouped`, { params }).then((r) => r.data);

export const getComparativeTrialBalance = (params) =>
  API.get(`${BASE}/trial-balance/comparative`, { params }).then((r) => r.data);

export const verifyTrialBalance = (params) =>
  API.get(`${BASE}/trial-balance/verify`, { params }).then((r) => r.data);

/* ── Double-Entry: Profit & Loss ── */
export const getProfitLoss = (params) =>
  API.get(`${BASE}/profit-loss`, { params }).then((r) => r.data);

export const getCategorisedProfitLoss = (params) =>
  API.get(`${BASE}/profit-loss/categorised`, { params }).then((r) => r.data);

export const getComparativeProfitLoss = (params) =>
  API.get(`${BASE}/profit-loss/comparative`, { params }).then((r) => r.data);

export const getProfitLossMonthlyTrend = (params) =>
  API.get(`${BASE}/profit-loss/monthly`, { params }).then((r) => r.data);

/* ── Double-Entry: Balance Sheet ── */
export const getBalanceSheet = (params) =>
  API.get(`${BASE}/balance-sheet`, { params }).then((r) => r.data);

export const getCategorisedBalanceSheet = (params) =>
  API.get(`${BASE}/balance-sheet/categorised`, { params }).then((r) => r.data);

export const getComparativeBalanceSheet = (params) =>
  API.get(`${BASE}/balance-sheet/comparative`, { params }).then((r) => r.data);

export const verifyAccountingEquation = (params) =>
  API.get(`${BASE}/balance-sheet/verify`, { params }).then((r) => r.data);

/* ── Double-Entry: Reports ── */
export const getFinancialSnapshot = (params) =>
  API.get(`${BASE}/reports/financial-snapshot`, { params }).then((r) => r.data);

export const getAuditTrail = (params) =>
  API.get(`${BASE}/reports/audit-trail`, { params }).then((r) => r.data);

export const getAccountActivity = (params) =>
  API.get(`${BASE}/reports/account-activity`, { params }).then((r) => r.data);

export const getSourceSummary = (params) =>
  API.get(`${BASE}/reports/source-summary`, { params }).then((r) => r.data);

export const getFundReconciliation = (params) =>
  API.get(`${BASE}/reports/fund-reconciliation`, { params }).then((r) => r.data);

export const getDayBook = (params) =>
  API.get(`${BASE}/reports/day-book`, { params }).then((r) => r.data);

/* ── Fund Contributions ── */
export const fetchContributions = (params) =>
  API.get(`${BASE}/contributions`, { params }).then((r) => r.data);

export const getContributionById = (id) =>
  API.get(`${BASE}/contributions/${id}`).then((r) => r.data);

export const createContribution = (data) =>
  API.post(`${BASE}/contributions`, data).then((r) => r.data);

export const updateContribution = (id, data) =>
  API.put(`${BASE}/contributions/${id}`, data).then((r) => r.data);

export const settleContribution = (id, data) =>
  API.post(`${BASE}/contributions/${id}/settle`, data).then((r) => r.data);

export const deleteContribution = (id) =>
  API.delete(`${BASE}/contributions/${id}`).then((r) => r.data);

export const getContributionSummary = () =>
  API.get(`${BASE}/contributions/summary`).then((r) => r.data);

export const getContributionRemaining = (caseId, params) =>
  API.get(`${BASE}/contributions/remaining/${caseId}`, { params }).then((r) => r.data);

/* ── Company Funds ── */
export const fetchFunds = () => API.get(`${BASE}/funds`).then((r) => r.data);
export const createFund = (data) => API.post(`${BASE}/funds`, data).then((r) => r.data);
export const updateFund = (fundId, data) => API.put(`${BASE}/funds/${fundId}`, data).then((r) => r.data);
export const getFundById = (fundId) => API.get(`${BASE}/funds/${fundId}`).then((r) => r.data);
export const getFundTransactions = (fundId) => API.get(`${BASE}/funds/${fundId}/transactions`).then((r) => r.data);
export const getFundBalance = (fundId) => API.get(`${BASE}/funds/${fundId}/balance`).then((r) => r.data);
export const getPaymentEnabledFunds = (purpose) =>
  API.get(`${BASE}/funds/payment-enabled`, { params: purpose ? { purpose } : {} }).then((r) => r.data);
export const loadFund = (fundId, data) => API.post(`${BASE}/funds/${fundId}/load`, data).then((r) => r.data);
export const toggleFundPaymentVisibility = (fundId) => API.patch(`${BASE}/funds/${fundId}/toggle-payment`).then((r) => r.data);
export const linkFundToAccount = (accountId, fundId) => API.patch(`${BASE}/accounts/${accountId}/link-fund`, { fundId }).then((r) => r.data);

/* ── Fund Operations ── */
export const payExpense = (expenseId, data) => 
  API.post(`${BASE}/expenses/${expenseId}/pay`, data).then((r) => r.data);

export const allocateProfitToFund = (fundId, data) => 
  API.post(`${BASE}/funds/${fundId}/allocate-profit`, data).then((r) => r.data);

export const allocateLossToFund = (fundId, data) => 
  API.post(`${BASE}/funds/${fundId}/allocate-loss`, data).then((r) => r.data);

export const manualFundTransaction = (fundId, data) => 
  API.post(`${BASE}/funds/${fundId}/manual-transaction`, data).then((r) => r.data);

export const transferBetweenFunds = (data) => 
  API.post(`${BASE}/funds/transfer`, data).then((r) => r.data);

/* ── Case P/L Ledger ── */
export const getCasePnlEntries = (params) =>
  API.get(`${BASE}/case-pnl`, { params }).then((r) => r.data);

export const getCasePnlByCase = (caseId) =>
  API.get(`${BASE}/case-pnl/${caseId}`).then((r) => r.data);

export const allocateCasePnl = (id, data) =>
  API.post(`${BASE}/case-pnl/${id}/allocate`, data).then((r) => r.data);

/* ── Expenses ── */
export const fetchExpenses = (params) =>
  API.get(`${BASE}/expenses`, { params }).then((r) => r.data);

export const getExpenseById = (id) =>
  API.get(`${BASE}/expenses/${id}`).then((r) => r.data);

export const createExpense = (data) =>
  API.post(`${BASE}/expenses`, data).then((r) => r.data);

export const updateExpense = (id, data) =>
  API.put(`${BASE}/expenses/${id}`, data).then((r) => r.data);

export const deleteExpense = (id) =>
  API.delete(`${BASE}/expenses/${id}`).then((r) => r.data);

export const getExpenseSummary = (params) =>
  API.get(`${BASE}/expenses/summary`, { params }).then((r) => r.data);

export const getExpenseBranchSummary = (params) =>
  API.get(`${BASE}/expenses/branch-summary`, { params }).then((r) => r.data);

/** One payment voucher (PDF blob), any status. */
export const fetchExpenseVoucherPdf = (id) =>
  API.get(`${BASE}/expenses/${id}/voucher`, { responseType: 'blob' }).then((r) => r.data);

/** Many vouchers in one PDF, two per A4 sheet. params: { ids: 'a,b' } or the list filters. */
export const fetchExpenseVouchersPdf = (params) =>
  API.get(`${BASE}/expenses/vouchers`, { params, responseType: 'blob' }).then((r) => r.data);

/* ── Users (for contributor dropdowns) ── */
export const fetchUsers = () =>
  API.get('/users').then((r) => r.data);

/* ── Stock Register ── */
export const fetchStockSummary = () =>
  API.get(`${BASE}/stock/summary`).then((r) => r.data);
export const fetchStockMovements = (params = {}) =>
  API.get(`${BASE}/stock/movements`, { params }).then((r) => r.data);
export const createStockMovement = (data) =>
  API.post(`${BASE}/stock/movements`, data).then((r) => r.data);
export const cancelStockMovement = (id) =>
  API.post(`${BASE}/stock/movements/${id}/cancel`).then((r) => r.data);

/* ── GST Centre ── */
export const fetchGstSummary = (params = {}) =>
  API.get(`${BASE}/gst/summary`, { params }).then((r) => r.data);
export const fetchGstOutput = (params = {}) =>
  API.get(`${BASE}/gst/output`, { params }).then((r) => r.data);
export const fetchGstInput = (params = {}) =>
  API.get(`${BASE}/gst/input`, { params }).then((r) => r.data);
export const fetchGstManual = (params = {}) =>
  API.get(`${BASE}/gst/manual`, { params }).then((r) => r.data);
export const createGstManualEntry = (data) =>
  API.post(`${BASE}/gst/manual`, data).then((r) => r.data);
export const cancelGstManualEntry = (id) =>
  API.post(`${BASE}/gst/manual/${id}/cancel`).then((r) => r.data);
